import { useContext, useLayoutEffect, useRef } from 'react';
import { createLocation, locationsAreEqual } from '../history';
import invariant from '../tiny-invariant';
import RouterContext from './RouterContext';
import generatePath from './generatePath';
import { LocationDescriptor, Location } from '../history';
import { match } from '.';

export interface RedirectProps {
  to: LocationDescriptor;
  push?: boolean;
  from?: string;
  path?: string;
  exact?: boolean;
  strict?: boolean;
}

/**
 * The public API for navigating programmatically with a component.
 */
function Redirect(props: RedirectProps) {
  const { to, push = false } = props;
  const computedMatch: match = (props as any).computedMatch;

  const context = useContext(RouterContext);
  invariant(context, 'You should not use <Redirect> outside a <Router>');

  const { history } = context;

  // Track the last resolved target so we can skip redundant navigations when
  // the component re-renders with an equivalent location (e.g. an inline object
  // `to` whose identity changes every render, or the fresh computedMatch
  // identity Switch injects). Preserves the original Lifecycle implementation's
  // mount + update-only-if-changed behavior.
  const prevLocationRef = useRef<Location>(null);

  // Deriving method/location inside the effect keeps the dependency list honest.
  useLayoutEffect(() => {
    const method = push ? history.push : history.replace;
    const location = createLocation(
      computedMatch
        ? typeof to === 'string'
          ? generatePath(to, computedMatch.params)
          : {
              ...to,
              pathname: generatePath(to.pathname ?? '/', computedMatch.params),
            }
        : to
    );

    const prevLocation = prevLocationRef.current;
    prevLocationRef.current = location;

    if (
      !prevLocation ||
      !locationsAreEqual(prevLocation, { ...location, key: prevLocation.key })
    ) {
      method(location);
    }
  }, [to, push, history, computedMatch]);

  return null;
}

export default Redirect;
